// pages/company/Login.tsx
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, AlertCircle, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useCompanyAuth } from '@/contexts/CompanyAuthContext';

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,400&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  :root {
    --c: #FAF8F4; --c2: #F2EDE3; --c3: #E6DFD1; --c4: #D9CFBD;
    --forest: #1C3830; --fmid: #2B5244;
    --gold: #C09A40; --gold-bg: #F5EECB;
    --stone: #796F61; --ink: #18150F; --white: #FFFFFF;
    --red: #B83232; --red-bg: #FEF0EE;
  }
  .lgn * { box-sizing: border-box; }
  .lgn {
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
    background: var(--c);
    display: flex; align-items: center; justify-content: center;
    padding: 24px 16px;
    position: relative; overflow: hidden;
  }
  .lgn::before {
    content: '';
    position: fixed; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 60% 50% at 80% 30%, rgba(192,154,64,.07) 0%, transparent 70%),
      radial-gradient(ellipse 50% 40% at 10% 80%, rgba(28,56,48,.05) 0%, transparent 60%);
  }
  ::selection { background: rgba(192,154,64,.2); color: var(--forest); }

  .lgn-card {
    width: 100%; max-width: 420px;
    background: var(--white);
    border: 1px solid var(--c3);
    border-radius: 20px;
    box-shadow: 0 12px 48px rgba(28,56,48,.10), 0 2px 8px rgba(28,56,48,.04);
    overflow: hidden;
    position: relative; z-index: 1;
  }

  .lgn-header {
    background: var(--forest);
    padding: 40px 36px 36px;
    text-align: center;
    position: relative; overflow: hidden;
  }
  .lgn-header::before {
    content: '';
    position: absolute; top: -40px; right: -40px;
    width: 160px; height: 160px; border-radius: 50%;
    background: rgba(192,154,64,.1);
  }
  .lgn-header::after {
    content: '';
    position: absolute; bottom: -50px; left: -20px;
    width: 130px; height: 130px; border-radius: 50%;
    background: rgba(192,154,64,.07);
  }
  .lgn-mark {
    width: 52px; height: 52px; border-radius: 14px;
    background: rgba(245,238,203,.12); border: 1.5px solid rgba(192,154,64,.3);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 18px; position: relative; z-index: 1;
  }
  .lgn-header h1 {
    font-family: 'Lora', serif; font-weight: 600;
    font-size: 24px; color: var(--c);
    letter-spacing: -.025em; margin: 0 0 6px;
    position: relative; z-index: 1;
  }
  .lgn-header p {
    font-size: 13.5px; color: rgba(250,248,244,.58);
    margin: 0; position: relative; z-index: 1;
  }

  .lgn-body { padding: 32px 32px 28px; }
  @media (max-width: 480px) { .lgn-body { padding: 24px 20px 20px; } .lgn-header { padding: 32px 20px 28px; } }

  .lgn-error {
    display: flex; align-items: flex-start; gap: 9px;
    background: var(--red-bg); border: 1px solid rgba(184,50,50,.2);
    border-radius: 9px; padding: 11px 13px; margin-bottom: 22px;
    font-size: 13px; color: var(--red); line-height: 1.45;
  }

  .lgn-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
  .lgn-label { font-size: 12.5px; font-weight: 600; color: var(--ink); letter-spacing: .01em; }

  .lgn-input-wrap { position: relative; }
  .lgn-input-icon {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%); pointer-events: none;
    color: var(--stone); opacity: .6;
  }
  .lgn-password-toggle {
    position: absolute; right: 12px; top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: var(--stone);
    opacity: .6;
    transition: opacity .14s;
    background: none;
    border: none;
    padding: 0;
  }
  .lgn-password-toggle:hover { opacity: 1; }
  .lgn-input {
    width: 100%; height: 44px;
    border: 1.5px solid var(--c3);
    border-radius: 9px; background: var(--white);
    font-size: 13.5px; font-family: 'DM Sans', sans-serif;
    color: var(--ink); transition: border-color .14s, box-shadow .14s;
    outline: none;
  }
  .lgn-input.email-input { padding: 0 14px 0 40px; }
  .lgn-input.password-input { padding: 0 40px 0 40px; }
  .lgn-input:focus {
    border-color: var(--gold);
    box-shadow: 0 0 0 3px rgba(192,154,64,.12);
  }
  .lgn-input:disabled { background: var(--c2); opacity: .6; }

  .lgn-row {
    display: flex; align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .lgn-remember { display: flex; align-items: center; gap: 8px; }
  .lgn-checkbox {
    width: 16px; height: 16px; border-radius: 4px;
    border: 1.5px solid var(--c4); background: var(--white);
    cursor: pointer; appearance: none; flex-shrink: 0;
    transition: all .14s;
  }
  .lgn-checkbox:checked {
    background: var(--gold); border-color: var(--gold);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'%3E%3Cpath d='M2 5l2.5 2.5 3.5-4' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: center;
  }
  .lgn-remember-label { font-size: 13px; color: var(--stone); cursor: pointer; }
  .lgn-forgot { font-size: 13px; color: var(--gold); text-decoration: none; }
  .lgn-forgot:hover { text-decoration: underline; }

  .lgn-submit {
    width: 100%; height: 46px;
    background: var(--forest); color: var(--c);
    border: none; border-radius: 10px;
    font-size: 14.5px; font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer; transition: all .15s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 3px 12px rgba(28,56,48,.2);
  }
  .lgn-submit:hover:not(:disabled) {
    background: var(--fmid);
    box-shadow: 0 5px 18px rgba(28,56,48,.26);
    transform: translateY(-1px);
  }
  .lgn-submit:disabled { opacity: .6; cursor: not-allowed; transform: none; }

  @keyframes lgn-spin { to { transform: rotate(360deg); } }
  .lgn-spinner {
    width: 15px; height: 15px; border-radius: 50%;
    border: 2px solid rgba(250,248,244,.3);
    border-top-color: var(--c);
    animation: lgn-spin .7s linear infinite;
  }

  .lgn-footer {
    text-align: center;
    border-top: 1px solid var(--c3);
    padding: 18px 24px;
    font-size: 13px; color: var(--stone);
  }
  .lgn-footer a { color: var(--gold); text-decoration: none; font-weight: 500; }
  .lgn-footer a:hover { text-decoration: underline; }
`;

function LgnStyles() {
  useEffect(() => {
    const id = 'lgn-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = STYLES;
      document.head.appendChild(el);
    }
  }, []);
  return null;
}

export default function CompanyLogin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, isLoading: authLoading, getRememberedEmail, saveRememberedEmail } = useCompanyAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  // Load saved email if remember me was checked
  useEffect(() => {
    const savedEmail = getRememberedEmail();
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, [getRememberedEmail]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(formData.email, formData.password, rememberMe);
      
      if (result.success) {
        if (rememberMe) {
          saveRememberedEmail(formData.email);
        }
        
        toast({
          title: 'Welcome!',
          description: `Logged in successfully`,
        });
        
        navigate('/company/dashboard');
      } else {
        setError(result.error || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loading = isLoading || authLoading;

  return (
    <>
      <LgnStyles />
      <div className="lgn">
        <div className="lgn-card">

          <div className="lgn-header">
            <div className="lgn-mark">
              <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="6" width="4" height="8" rx="0.5" fill="#C09A40" opacity="0.9"/>
                <rect x="6" y="3" width="4" height="11" rx="0.5" fill="#F5EECB"/>
                <rect x="10" y="5" width="4" height="9" rx="0.5" fill="#C09A40" opacity="0.7"/>
                <rect x="7" y="10" width="2" height="4" rx="0.3" fill="#C09A40"/>
              </svg>
            </div>
            <h1>Company Portal</h1>
            <p>Access your company dashboard</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="lgn-body">
              {error && (
                <div className="lgn-error">
                  <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  {error}
                </div>
              )}

              <div className="lgn-field">
                <label className="lgn-label">Email Address</label>
                <div className="lgn-input-wrap">
                  <Mail size={14} className="lgn-input-icon" />
                  <input
                    className="lgn-input email-input"
                    type="email"
                    placeholder="company@example.com"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="lgn-field">
                <label className="lgn-label">Password</label>
                <div className="lgn-input-wrap">
                  <Lock size={14} className="lgn-input-icon" />
                  <input
                    className="lgn-input password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="lgn-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="lgn-row">
                <div className="lgn-remember">
                  <input
                    type="checkbox"
                    id="remember"
                    className="lgn-checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  <label htmlFor="remember" className="lgn-remember-label">Remember me</label>
                </div>
                <Link to="/company/forgot-password" className="lgn-forgot">Forgot password?</Link>
              </div>

              <button type="submit" className="lgn-submit" disabled={loading}>
                {loading
                  ? <><div className="lgn-spinner" /> Signing in…</>
                  : <>Sign In <ArrowRight size={16} /></>
                }
              </button>
            </div>

            <div className="lgn-footer">
              Don't have an account?{' '}
              <Link to="/company/register">Register your company</Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}